/*
  PHASE 28 — shared website (Catalog mode) ka "plan": super admin ne is hospital ko
  kaunse module diye hain. (Backend: Tenancy/ModulePlan.cs · PLAN.md Phase 28)

  ─────────────────────────────────────────────────────────────────────────────
  DO MODE

    Single  — Kamla / keycodehms jaisi akeli website. Koi plan nahi, sab module —
              yahan ki har jaanch "haan" deti hai, yaani aaj jaisa hi.
    Catalog — nayi shared website. Login response me `entitledModules` aata hai
              (HMS diya ho to saare core module — OPD, IPD, Billing… — bhi usme hote hain).

  ⚠️ Ye SIRF UI ka aaram hai (menu chhupana, darwaze par saaf sandesh). Asli rok
     backend par hai — TenantResolutionMiddleware + PermissionService har API par
     plan jaanchte hain. sessionStorage badal kar menu dikh bhi jaaye to API 403 degi.
*/

const KEY_MODE = "tenancyMode";
const KEY_ENT = "entitledModules";
const KEY_TENANT = "tenantId";
const KEY_CODE = "tenantCode";
const KEY_DEMO = "sharedDemo";

// Alag product — HMS ke saath apne aap NAHI aate (backend ModulePlan.Products jaisa hi)
export const PRODUCT_MODULES = ["PHARMACY", "INVENTORY", "HRMS", "IPD WARD"];

const norm = (m) => String(m ?? "").trim().toUpperCase();

// Login ke turant baad — response jaisa aaya waisa rakh do.
export function saveTenancy(data) {
  try {
    const catalog = data?.tenancyMode === "Catalog";
    sessionStorage.setItem(KEY_MODE, catalog ? "Catalog" : "Single");
    if (catalog) {
      const ent = Array.isArray(data.entitledModules) ? data.entitledModules : [];
      sessionStorage.setItem(KEY_ENT, JSON.stringify(ent));
      if (data.tenantId != null) sessionStorage.setItem(KEY_TENANT, String(data.tenantId));
      if (data.tenantCode) sessionStorage.setItem(KEY_CODE, String(data.tenantCode));
      // Sabka EK shared demo hospital (19 Sep 2026) — kone me "Shared demo" (DemoBadge)
      if (data.isDemo) sessionStorage.setItem(KEY_DEMO, "1");
      else sessionStorage.removeItem(KEY_DEMO);
    } else {
      sessionStorage.removeItem(KEY_ENT);
      sessionStorage.removeItem(KEY_TENANT);
      sessionStorage.removeItem(KEY_CODE);
      sessionStorage.removeItem(KEY_DEMO);
    }
  } catch {
    /* sessionStorage na mile to kuch nahi — backend waise bhi rokta hai */
  }
}

export function isCatalog() {
  try {
    return sessionStorage.getItem(KEY_MODE) === "Catalog";
  } catch {
    return false;
  }
}

export function tenantCode() {
  try {
    return sessionStorage.getItem(KEY_CODE) || "";
  } catch {
    return "";
  }
}

// null = pata nahi (Single, ya padh na paaye) -> koi rok nahi
function entitledSet() {
  if (!isCatalog()) return null;
  try {
    const raw = sessionStorage.getItem(KEY_ENT);
    if (!raw) return null;
    const list = JSON.parse(raw);
    return Array.isArray(list) ? new Set(list.map(norm)) : null;
  } catch {
    return null;
  }
}

// Ye EK module hospital ke plan me hai kya? (naam — "Pharmacy", "OPD", "IPD WARD"…)
export function isEntitled(name) {
  const set = entitledSet();
  if (!set) return true;
  return set.has(norm(name));
}

// Menu ka button / screen ka darwaza — ek naam ya naamon ki soochi
// (jaise Sidebar ka ["Inventory", "HMS"] = "Inventory ka menu; HMS wale ko bhi dikhe").
//
//   • soochi me koi ALAG PRODUCT hai (Pharmacy/Inventory/HRMS/IPD Ward) -> wahi product
//     plan me hona chahiye. HMS wala saathi naam use nahi khol sakta — backend bhi nahi kholta.
//   • sirf core module hain -> unme se koi bhi plan me ho to kaafi.
export function menuEntitled(required) {
  if (!entitledSet()) return true;
  const list = (Array.isArray(required) ? required : [required]).map(norm).filter(Boolean);
  if (list.length === 0) return true;
  const product = list.find((m) => PRODUCT_MODULES.includes(m));
  if (product) return isEntitled(product);
  return list.some((m) => isEntitled(m));
}

// Plan me koi core (HMS wala) module hai ya sirf alag product?
export function hasCorePlan() {
  const set = entitledSet();
  if (!set) return true;
  return [...set].some((m) => !PRODUCT_MODULES.includes(m));
}

// Central Master (/master/...) ka kaunsa page is plan me khulta hai.
//
//   users / roles / permissions      -> hamesha (hospital ko apne user banane hi hain)
//   department                       -> hamesha (HRMS aur Inventory bhi isi par chalte hain)
//   dawa ka master (medicine, brand,  -> Pharmacy YA HMS (core) — prescription me bhi dawa
//   category, pharma company)            chahiye (backend: ModuleAreas.SharedMasters)
//   vendor                            -> sirf Pharmacy (dawa ki kharid ka supplier)
//   baaki (charge, room, department…) -> HMS (core) plan
export function masterPathAllowed(path) {
  if (!entitledSet()) return true;
  const p = String(path || "").toLowerCase();
  if (!p.startsWith("/master/")) return true;
  if (/^\/master\/(user\/|users-list|roles-list|role-model|department)/.test(p)) return true;
  if (/^\/master\/vendor/.test(p)) return isEntitled("PHARMACY");
  if (/^\/master\/(medicine-|pharma-company)/.test(p)) return isEntitled("PHARMACY") || hasCorePlan();
  return hasCorePlan();
}

// Login ke baad pehla page — sirf-product wale plan par HMS dashboard nahi (wahan har
// card 403 hota). null = aam raasta (Login.jsx apna faisla kare).
export function productLanding() {
  if (!entitledSet() || hasCorePlan()) return null;
  if (isEntitled("PHARMACY")) return "/pharmacy/sale";
  if (isEntitled("HRMS")) return "/hrms/dashboard";
  if (isEntitled("INVENTORY")) return "/inventory/dashboard";
  if (isEntitled("IPD WARD")) return "/ward/dashboard";
  return null;
}

// Shared demo hospital me hain? (shared website). null = demo nahi (ya Single mode).
// 19 Sep 2026 se demo EK hi hai aur uska login bahut logon ko diya jaata hai — koi tarikh nahi;
// us login ka password / naye login demo ke andar se nahi badalte (backend rokta hai).
export function demoState() {
  if (!isCatalog()) return null;
  try {
    return sessionStorage.getItem(KEY_DEMO) === "1" ? { shared: true } : null;
  } catch {
    return null;
  }
}

// Server ka error text — {message} JSON ho ya saada text — user ko dikhane layak.
export function readServerMessage(text, fallback) {
  if (!text) return fallback;
  try {
    const j = JSON.parse(text);
    if (typeof j === "string") return j || fallback;
    if (j && typeof j === "object") return typeof j.message === "string" && j.message ? j.message : fallback;
  } catch {
    /* saada text */
  }
  const t = String(text).trim();
  if (!t || t.length > 200 || t.startsWith("<")) return fallback;
  return t;
}
